import { useRef } from "react";
import type { CSSProperties, ReactElement } from "react";
import { TextMorph } from "torph/react";
import {
  DISSOLVE_MS,
  ICON_IN_MS,
  LOADING_CHECKOUT,
  MORPH_MS,
  PAY_STATUS_LABEL,
} from "@libs/checkout-core";
import type { PayPhase, PayStatus, StatusLine, StatusMark } from "@libs/checkout-core";
import { useFlip } from "./flip";
import {
  DISSOLVE_EASE,
  ICON_IN_EASE,
  MORPH_EASE,
  MOVE_EASE,
  SETTLE_EASE,
} from "./motion";
import type { StatusOrigin } from "@libs/checkout-core";

/**
 * The Pay button's second life: once pressed it becomes the payment's status,
 * after torph's "processing transaction" example — the label morphs, the icon
 * swaps with a spring. Green is kept for the end: only "You're going" is done.
 * The words, timings and curves are the core's (`@libs/checkout-core`), so
 * the native Pay button tells the same story at the same pace.
 */
export type { PayPhase, PayStatus } from "@libs/checkout-core";
export { FOLD_DELAY_MS, FOLD_MS } from "@libs/checkout-core";

export { MOVE_EASE } from "./motion";

const KEYFRAMES = `
@keyframes lc-status-icon-in { from { opacity: 0; transform: scale(.6); filter: blur(2px) } to { opacity: 1; transform: scale(1); filter: blur(0) } }
[data-lc-pay]:not(:disabled):active { transform: scale(.97) }
@keyframes lc-status-spin { to { transform: rotate(360deg) } }
@media (prefers-reduced-motion: reduce) {
  [data-lc-status-icon] { animation: none !important }
  [data-lc-status-icon] svg g { animation: none !important }
}`;

/** The status icon's size, beside the words. */
const ICON = 22;
/** "You're going", while it still stands where Pay was: the tick's green,
 *  deepened enough to read as text on white. */
const DONE_TEXT = "#248a3d";

function Spinner() {
  return (
    <svg
      width={ICON}
      height={ICON}
      viewBox="0 0 23 23"
      fill="none"
      aria-hidden="true"
    >
      <g
        style={{
          animation: "lc-status-spin .6s linear infinite",
          transformOrigin: "50% 50%",
        }}
      >
        <path
          d="M21.313 11.4062C21.313 16.8775 16.8777 21.3128 11.4065 21.3128"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <path
          opacity="0.15"
          d="M11.4065 21.313C16.8777 21.313 21.313 16.8777 21.313 11.4065C21.313 5.93529 16.8777 1.5 11.4065 1.5C5.93529 1.5 1.5 5.93529 1.5 11.4065C1.5 16.8777 5.93529 21.313 11.4065 21.313Z"
          stroke="currentColor"
          strokeWidth="3"
        />
      </g>
    </svg>
  );
}

/** "You're going": the green tick, beside the green words. */
function Tick() {
  return (
    <svg
      width={ICON}
      height={ICON}
      viewBox="0 0 21 21"
      fill="none"
      aria-hidden="true"
    >
      <circle
        cx="10.45"
        cy="10.45"
        r="10.45"
        fill="#34c759"
      />
      <path
        d="M6.1 10.98L8.84 13.64L14.8 7.84"
        stroke="#fff"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** A prepared checkout that stopped before Pay: finished, in the button's
 *  blue — never the green that means tickets were issued. */
function Ready() {
  return (
    <svg
      width={ICON}
      height={ICON}
      viewBox="0 0 21 21"
      fill="none"
      aria-hidden="true"
    >
      <circle
        cx="10.45"
        cy="10.45"
        r="9.45"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M6.6 10.9L9 13.2L14.3 8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Nothing more will happen without the buyer. */
function Stopped() {
  return (
    <svg
      width={ICON}
      height={ICON}
      viewBox="0 0 21 21"
      fill="none"
      aria-hidden="true"
    >
      <circle
        cx="10.45"
        cy="10.45"
        r="9.45"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M8.2 7.2v6.5M12.7 7.2v6.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

const MARKS: Record<StatusMark, () => ReactElement> = {
  spinner: Spinner,
  done: Tick,
  ready: Ready,
  stopped: Stopped,
};

/**
 * The primary button, and once pressed the payment's status. The spinner
 * appears inside it, beside the words; as the checkout folds away the fill
 * fades and the words stay as plain text in its blue — spinner, tick, and
 * green (tick and words) only at the end.
 */
export function PayStatusButton({
  status,
  line = null,
  phase,
  idle,
  words,
  labels = PAY_STATUS_LABEL,
  loading = false,
  disabled,
  onClick,
  style,
}: {
  status: PayStatus | null;
  /** A status told by someone other than the engine's payment (a vendor's
   *  prepared checkout): its words and mark, on the same button and beats.
   *  Takes the place of `status` whenever it is set. */
  line?: StatusLine | null;
  /** Where the press has got to (the core's pay timeline); null before it. */
  phase: PayPhase | null;
  /** The button's own words before it is pressed; they morph into the status. */
  idle: string;
  /** Words that stand in for the status's own while the payment waits on
   *  something the buyer should know about. */
  words?: string | null;
  /** The status's words for each step: the tickets' unless the cart sells
   *  something else (`CHECKOUT_WORDS`). */
  labels?: Readonly<Record<PayStatus, string>>;
  /** The checkout cannot take a payment yet (the buyer's attestation, the
   *  card fields): "Loading checkout" with the spinner, as the button has
   *  said since Continue on every client. */
  loading?: boolean;
  disabled: boolean;
  onClick: () => void;
  style: CSSProperties;
}) {
  // The spinner stays until the tickets are the buyer's: a tick at
  // "Payment accepted" read as done while tickets were still being issued.
  const moving = line !== null || status !== null;
  const waiting = loading && !moving;
  const mark: StatusMark | null = line
    ? line.mark
    : status === "done"
      ? "done"
      : status !== null || waiting
        ? "spinner"
        : null;
  const Icon = mark ? MARKS[mark] : null;
  const icon = Icon ? <Icon /> : null;
  const done = mark === "done";
  // From the fold on, the button is only its words, in its own blue —
  // green once it is done, to match the tick beside them.
  const plain = phase === "folding";
  const wordsRef = useRef<HTMLSpanElement>(null);
  // The spinner arriving nudges the words aside: they glide, not jump.
  useFlip(wordsRef, Boolean(icon), { easing: MOVE_EASE });
  const color = !plain
    ? style.color
    : done
      ? DONE_TEXT
      : mark === "stopped"
        ? "var(--lc-muted)"
        : "var(--lc-button-background)";
  return (
    <>
      <style>{KEYFRAMES}</style>
      <button
        type="button"
        data-lc-pay=""
        disabled={disabled || waiting || moving}
        aria-live="polite"
        onClick={onClick}
        style={{
          ...style,
          // The fill is its own layer (below), so it can dissolve rather
          // than fade in place behind the words.
          background: "transparent",
          border: "var(--lc-border-width) var(--lc-border-style) transparent",
          boxShadow: "none",
          color,
          isolation: "isolate",
          position: "relative",
          alignItems: "center",
          columnGap: icon ? 10 : 0,
          cursor: moving || waiting ? "default" : style.cursor,
          display: "grid",
          gridTemplateAreas: '"icon words"',
          justifyContent: "center",
          opacity: moving || waiting ? 1 : style.opacity,
          transition: [
            // The words turn to the fill's blue a beat after it starts to
            // go, as it leaves — never white on a washed-out blue.
            `color ${DISSOLVE_MS - 150}ms ${SETTLE_EASE} ${plain ? 150 : 0}ms`,
            "opacity 300ms ease-out",
            // The press itself: answered on pointer-down.
            "transform 160ms ease-out",
          ].join(", "),
        }}
      >
        <span
          aria-hidden="true"
          style={{
            background: style.background,
            border: style.border,
            borderRadius: "inherit",
            boxShadow: style.boxShadow,
            boxSizing: "border-box",
            // Covers the border box the button's own border drew.
            inset: "calc(var(--lc-border-width, 1px) * -1)",
            position: "absolute",
            zIndex: -1,
            // Pressed and folding: the fill draws in, softens and goes —
            // a surface leaving, not a colour fading.
            filter: plain ? "blur(8px)" : "blur(0)",
            opacity: plain ? 0 : 1,
            transform: plain ? "scale(0.94)" : "none",
            transition: `opacity ${DISSOLVE_MS}ms ${DISSOLVE_EASE}, transform ${DISSOLVE_MS}ms ${DISSOLVE_EASE}, filter ${DISSOLVE_MS}ms ${DISSOLVE_EASE}`,
            // Its own compositing layer: the blur animates without repainting
            // the words above it (Safari especially).
            willChange: "opacity, transform, filter",
          }}
        />
        {icon ? (
          <span
            data-lc-status-mark=""
            style={{ display: "grid", gridArea: "icon" }}
          >
            <span
              // Keyed by the icon, not the words: the one spinner keeps
              // turning through processing, accepted and issuing.
              key={mark ?? "none"}
              data-lc-status-icon=""
              style={{
                animation:
                  `lc-status-icon-in ${ICON_IN_MS}ms ${ICON_IN_EASE} both`,
                display: "grid",
                placeItems: "center",
              }}
            >
              {icon}
            </span>
          </span>
        ) : null}
        <span
          ref={wordsRef}
          data-lc-status-words=""
          style={{ gridArea: "words" }}
        >
          <TextMorph duration={MORPH_MS} ease={MORPH_EASE}>
            {line?.words ?? words ??
              (status
                ? labels[status]
                : waiting
                  ? LOADING_CHECKOUT
                  : idle)}
          </TextMorph>
        </span>
      </button>
    </>
  );
}

/**
 * Where the status's tick and words are drawn right now, in this frame's
 * viewport: handed to the page with `succeeded`, so its success screen can
 * take "You're going" over from exactly here.
 */
export function statusOrigin(): StatusOrigin | null {
  const rect = (selector: string) => {
    const r = document
      .querySelector("[data-lc-pay] " + selector)
      ?.getBoundingClientRect();
    return r && r.width > 0
      ? { x: r.left, y: r.top, width: r.width, height: r.height }
      : null;
  };
  const mark = rect("[data-lc-status-mark]");
  const words = rect("[data-lc-status-words]");
  return mark && words ? { mark, words } : null;
}
